import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import spinner from '../../../assets/spinner.json';
import creditCardNavyIcon from '../../../assets/bank-card-navy.svg';
import creditCardIcon from '../../../assets/bank-card.png';
import bankAccountIcon from '../../../assets/bank-active.svg';
import infoIcon from '../../../assets/info.png';
import undoIcon from '../../../assets/undo-navy.svg';
import warningIcon from '../../../assets/warning.svg';

import {
  PAYMENT_CATEGORY,
  PAYMENT_TYPE,
} from '../../../constants';
import { PAYMENT_METHOD_TYPE_CHANGE } from '../../../actions/types';

import {
  paymentMethodChanged,
  validateDiamondUser,
} from '../../../actions';
import {
  getStripeKeys,
  undoDefaultPaymentMethod,
  updatePaymentMethod,
} from '../../../actions/stripe';
import FigoLottie from '../FigoLottie';

import './StripeEditDetails.css';

const ORIGIN_EB = 2;

export const StripeEditDetails = ({
  handleFscLeadCalls = () => { },
  noEdit = false,
  setShowStripeForm,
  styleUpdate,
}) => {
  const store = useSelector(({ quoting }) => (quoting), shallowEqual);
  const {
    newCustomer: { email, emailValidated },
    stripe,
  } = store;
  const dispatch = useDispatch();
  const updateButtonRef = useRef();
  const { t } = useTranslation('quoting');

  const [loading, setLoading] = useState(false);

  const renderTwoPayment = stripe.isTwoDefaults
    && !stripe.customerUpdated;

  const onDefaultPaymentMethodUpdated = (data) => () => {
    const { paymentCategory } = data;
    dispatch(updatePaymentMethod(data));
    const paymentMethodSelected =
      paymentCategory === PAYMENT_CATEGORY.bankAccount
        ? PAYMENT_TYPE.bank
        : PAYMENT_TYPE.creditCard;

    if (store.paymentMethodType?.value !== paymentMethodSelected.value) {
      dispatch(paymentMethodChanged({
        type: PAYMENT_METHOD_TYPE_CHANGE,
        value: paymentMethodSelected,
      }));

      handleFscLeadCalls();
    }
  };

  const onAddPaymentMethod = () => {
    if (!emailValidated) {
      dispatch(validateDiamondUser({ email }));
      return;
    }

    setLoading(true);

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.REACT_APP_RECAPTCHA_KEY, { action: 'submit' })
        .then((token) => {
          dispatch(getStripeKeys({ origin: ORIGIN_EB, recaptchaToken: token }));

          setShowStripeForm(true);

          dispatch(updatePaymentMethod({
            lastFourDigits: '',
            paymentCategory: '',
            token: '',
          }));
        });
    });
  };

  const renderUpdateButton = ({
    className = '',
    text,
    centerButtom = true,
  }) => {
    if (noEdit) {
      return null;
    }

    if (loading) {
      return centerButtom
        ? (
          <div className="Stripe-button-loading">
            <FigoLottie
              animationData={spinner}
              height={30}
              width={100}
            />
          </div>
        ) : (
          <FigoLottie
            animationData={spinner}
            height={30}
            width={100}
          />
        );
    }

    return (
      <button
        ref={updateButtonRef}
        aria-haspopup="dialog"
        className={`Stripe-details-button ${className}`}
        onClick={onAddPaymentMethod}
        type="button"
      >
        {text}
      </button>
    );
  };

  const renderOneItemDetails = ({
    icon,
    lastFourDigits,
    subTitle,
    title = '',
  }) => (
    <div className="Stripe-details Stripe-detail-update-style">
      <div className="Stripe-details-left">
        <img alt="" className="Stripe-details-image" src={icon} />

        <div aria-live={store.stripe.customerUpdated ? 'polite' : 'off'}>
          <strong>{title.toUpperCase()}</strong>

          <span className="Stripe-last-digits">
            {lastFourDigits}
          </span>

          <span>{subTitle}</span>
        </div>
      </div>

      {renderTwoPayment
        ? null
        : renderUpdateButton({
          className: 'Stripe-details-button-update ',
          text: t('payment.edit'),
        })}
    </div>
  );

  const renderCreditCardDetails = (defaultLastDigits) => {
    if (!store.stripe.paymentMethod.token) {
      return null;
    }

    const { paymentMethod } = store.stripe;

    const subtitle = paymentMethod.cardExpirationMonth
      ? t(
        'payment.cardFormat',
        {
          mount: paymentMethod.cardExpirationMonth,
          year: String(paymentMethod.cardExpirationYear).substring(2),
        },
      ) : '';

    const details = {
      className: 'Stripe-details-image',
      icon: styleUpdate ? creditCardNavyIcon : creditCardIcon,
      lastFourDigits: t('payment.cardEnding', {
        lastDigits: defaultLastDigits || paymentMethod.lastFourDigits,
      }),
      subTitle: subtitle,
      title: paymentMethod.cardType || t('payment.carTitle'),
    };

    return renderOneItemDetails(details);
  };

  const renderBankAccountDetails = (defaultLastDigits) => {
    if (!store.stripe.paymentMethod.token) {
      return null;
    }

    const { paymentMethod } = store.stripe;

    const subtitle = paymentMethod.accountType;
    const details = {
      className: 'Stripe-details-image',
      icon: bankAccountIcon,
      lastFourDigits: t('payment.accountEnding', {
        lastDigits: defaultLastDigits || paymentMethod.lastFourDigits,
      }),
      subTitle: subtitle,
      title: t('payment.backAccountTitle'),
    };

    return renderOneItemDetails(details);
  };

  const renderCheckBox = ({
    checked,
    MaskedAccountNumber,
    MaskedCardNumber,
    paymentCategory,
    PaymentProfileId,
  }) => {
    const isCreditCard = paymentCategory === PAYMENT_CATEGORY.creditCard;
    const defaultLastDigits = isCreditCard
      ? MaskedCardNumber : MaskedAccountNumber;
    const renderDetails = isCreditCard
      ? renderCreditCardDetails : renderBankAccountDetails;

    return (
      <div className="Stripe-radio-button-container">
        <label htmlFor={isCreditCard ? 'rb-credit-card' : 'rb-bank'}>
          <input
            checked={checked}
            id={isCreditCard ? 'rb-credit-card' : 'rb-bank'}
            name="paymentmethod"
            onChange={onDefaultPaymentMethodUpdated({
              lastFourDigits: isCreditCard
                ? MaskedCardNumber : MaskedAccountNumber,
              paymentCategory,
              token: PaymentProfileId,
            })}
            type="radio"
            value={isCreditCard
              ? PAYMENT_TYPE.creditCard.value
              : PAYMENT_TYPE.bank.value}
          />

          {renderDetails(defaultLastDigits)}
        </label>
      </div>
    );
  };

  function renderTwoCardDetails() {
    const isCreditCard = store.stripe
      .paymentMethod.paymentCategory === PAYMENT_CATEGORY.creditCard;

    const { bankAccount, creditCard } = store.stripe.defaultsPaymentMethod;
    const { paymentCategory } = store.stripe.paymentMethod;

    if (renderTwoPayment) {
      return (
        <fieldset>
          <div className="Stripe-button-select">
            <div className="Flex">
              {renderCheckBox({
                checked: paymentCategory === PAYMENT_CATEGORY.bankAccount,
                paymentCategory: PAYMENT_CATEGORY.bankAccount,
                ...bankAccount,
              })}
            </div>
          </div>

          <div className="Stripe-button-select">
            <div className="Flex">
              {renderCheckBox({
                checked: paymentCategory === PAYMENT_CATEGORY.creditCard,
                paymentCategory: PAYMENT_CATEGORY.creditCard,
                ...creditCard,
              })}
            </div>
          </div>

          {renderUpdateButton({
            centerButtom: false,
            text: t('payment.useADifferentPaymentMethod'),
          })}
        </fieldset>
      );
    }

    return isCreditCard
      ? renderCreditCardDetails() : renderBankAccountDetails();
  }

  const onUndoChange = () => {
    const { defaultsPaymentMethod, isTwoDefaults } = store.stripe;

    const isCreditCard = defaultsPaymentMethod.defaultValue
      === PAYMENT_CATEGORY.creditCard;
    const data = isCreditCard
      ? defaultsPaymentMethod.creditCard : defaultsPaymentMethod.bankAccount;

    dispatch(undoDefaultPaymentMethod({
      isCreditCard,
      isTwoDefaults,
      paymentMethod: data,
    }));
  };

  const renderWarning = () => {
    const {
      paymentMethod,
      customerUpdated,
      petPaymentMethods,
    } = store.stripe;

    if (!customerUpdated) {
      return null;
    }

    const { paymentCategory } = paymentMethod;
    const pets = petPaymentMethods
      .filter((item) => item.Value === paymentCategory)
      .map((item) => item.Key);
    let petsNames = '';

    if (pets.length === 0) {
      return null;
    }

    if (pets.length === 1) {
      petsNames = pets;
    } else if (pets.length === 2) {
      petsNames = pets.join(t('payment.petsJoin'));
    } else {
      const firtsPets = pets
        .filter((_, index) => index < pets.length - 1).join(', ');
      const endPet = pets[pets.length - 1];

      petsNames = t('payment.petName', { endPet, firstPet: firtsPets });
    }

    const isCreditCard =
      paymentCategory === PAYMENT_CATEGORY.creditCard;
    const paymentType = isCreditCard ? t('payment.paymentTypeCard')
      : t('payment.paymentTypeAccount');

    return (
      <div className="Stripe-modal-warning-container">
        <img alt="" src={infoIcon} />

        <div className="Stripe-modal-warning-right">
          <p>
            {t('payment.paymentWarning', { paymentType })}

            <span> {petsNames}</span>.
          </p>

          {!styleUpdate && (
            <button onClick={onUndoChange} type="button">
              <img alt="" src={undoIcon} />

              <span>{t('payment.undoChanges')}</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderChargeWarning =
    useCallback(() => store.stripe.paymentMethod.token && (
      <div className="Stripe-section-warning">
        <img alt="" src={warningIcon} />

        {t('payment.notChargedYet')}
      </div>
    ), [store.stripe.paymentMethod.token, t]);

  return (
    <div
      className="Stripe-container"
      id="existent-methods-container"
      tabIndex={-1}
    >
      {renderTwoCardDetails()}

      {renderWarning()}

      {renderChargeWarning()}
    </div>
  );
};
